import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm } from "@mantine/form";
import {
  Button,
  Group,
  Tabs,
  TextInput,
  Textarea,
  Select,
  Stack,
  Switch,
  Paper,
  NumberInput,
  Box,
  Tooltip,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { Page, FormSectionCard } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabActionsOptional, notifyListRefresh } from "../../context/tabContext";
import { buildTabKey } from "../../utils/tabNavigation";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import McaStepBuilder from "../../components/mca/McaStepBuilder";
import McaWatchFieldsEditor, {
  rowsToWatchFields,
  watchFieldsToRows,
} from "../../components/mca/McaWatchFieldsEditor";
import {
  MCA_ENTITIES,
  MCA_ACTIONS,
  MCA_TRIGGER_TYPES,
  MCA_WATCH_FIELD_LOGIC,
  migrateStepNotifications,
  migrateStepFields,
  validateStepNotification,
} from "../../data/mcaEntities";
import { useMcaSubmitAction } from "../../hooks/useMcaSubmitAction";
import { McaEntityBanner } from "../../components/mca/McaEntityIndicators";
import { isEntityMcaLocked } from "../../components/mca/mcaApprovalLabels";

const apiEntity = "mcaProcess";
const MCA_PROCESS_LIST_PATH = "/management/mcaProcess";

const toRefId = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v;
  return v._id ?? null;
};

const normalizeNotificationPayload = (n) => {
  const notifyBy = n.notifyBy || "USERS";
  return {
    name: n.name?.trim(),
    channel: n.channel || "EMAIL",
    notifyOnStart: !!n.notifyOnStart,
    notifyOnComplete: n.notifyOnComplete !== false,
    notifyBy,
    notifyEmails:
      notifyBy === "EMAILS"
        ? (n.notifyEmails ?? []).map((e) => String(e).trim()).filter(Boolean)
        : [],
    notifyUsers:
      notifyBy === "USERS"
        ? (n.notifyUsers ?? []).map(toRefId).filter(Boolean)
        : [],
    notifyRoles:
      notifyBy === "USER_ROLE"
        ? (n.notifyRoles ?? []).map(toRefId).filter(Boolean)
        : [],
    notifyMaker: !!n.notifyMaker,
    notifyNextApprovers: !!n.notifyNextApprovers,
    subject:
      (n.channel || "EMAIL") === "EMAIL" ? n.subject?.trim() : undefined,
    message:
      (n.channel || "EMAIL") === "EMAIL" ? n.message?.trim() : undefined,
  };
};

const migrateFinalActionToSteps = (steps, finalAction) => {
  const hasStepSetValues = steps.some((s) => (s.setValues ?? []).length > 0);
  if (hasStepSetValues) return steps;

  const legacyStatus = finalAction?.setStatus?.trim();
  const legacyFields = finalAction?.setFields ?? {};
  if (!legacyStatus && !Object.keys(legacyFields).length) return steps;

  const migrated = steps.map((s) => ({
    ...s,
    setValues: (s.setValues ?? []).map((v) => ({ ...v })),
  }));
  const lastIdx = migrated.length - 1;
  if (lastIdx < 0) return migrated;

  const setValues = [...(migrated[lastIdx].setValues ?? [])];
  if (legacyStatus) setValues.push({ field: "status", value: legacyStatus });
  Object.entries(legacyFields).forEach(([field, value]) => {
    setValues.push({ field, value });
  });
  migrated[lastIdx] = { ...migrated[lastIdx], setValues };
  return migrated;
};

const buildPayload = (formValues, watchRows) => ({
  name: formValues.name?.trim(),
  cname: formValues.cname?.trim() || undefined,
  description: formValues.description?.trim() || undefined,
  status: formValues.status,
  triggerType: formValues.triggerType,
  priority: Number(formValues.priority ?? 0),
  triggerConfig: {
    targetEntity: formValues.triggerConfig.targetEntity,
    action: formValues.triggerConfig.action,
    watchFieldLogic: formValues.triggerConfig.watchFieldLogic ?? "OR",
    watchFields: rowsToWatchFields(watchRows).length
      ? rowsToWatchFields(watchRows)
      : formValues.triggerConfig.watchFields ?? [],
    allowedMakers: (formValues.triggerConfig.allowedMakers ?? [])
      .map(toRefId)
      .filter(Boolean),
    allowedMakerRoles: (formValues.triggerConfig.allowedMakerRoles ?? [])
      .map(toRefId)
      .filter(Boolean),
  },
  steps: (formValues.steps ?? []).map((step, idx) => ({
    order: step.order ?? idx + 1,
    name: step.name?.trim(),
    description: step.description?.trim() || undefined,
    approvalType: step.approvalType || "ANY",
    approvers: (step.approvers ?? []).map(toRefId).filter(Boolean),
    approverRoles: (step.approverRoles ?? []).map(toRefId).filter(Boolean),
    checklist: (step.checklist ?? [])
      .filter((c) => c.title?.trim())
      .map((c) => ({
        title: c.title.trim(),
        hint: c.hint?.trim() || undefined,
        required: !!c.required,
      })),
    setValues: (step.setValues ?? [])
      .filter((v) => v.field?.trim())
      .map((v) => ({
        field: v.field.trim(),
        value: v.value,
      })),
    notifications: (step.notifications ?? [])
      .filter((n) => n.name?.trim())
      .map(normalizeNotificationPayload),
    stepFields: (step.stepFields ?? [])
      .filter((f) => f.label?.trim())
      .map((f) => ({
        label: f.label.trim(),
        cname: f.cname?.trim() || undefined,
        inputType: f.inputType || "TEXT",
        required: !!f.required,
      })),
    includeWhen: step.includeWhen?.field
      ? {
          field: step.includeWhen.field.trim(),
          condition: step.includeWhen.condition,
        }
      : undefined,
    timeoutHours: step.timeoutHours || undefined,
  })),
  finalAction: {
    setStatus: undefined,
    setFields: {},
  },
});

const getSaveErrorMessage = (api, res) => {
  if (typeof res?.error === "string") return res.error;
  const fromError = api.getErrorMsg(res?.error);
  if (fromError) return fromError;
  return res?.message || "Save failed";
};

const defaultValues = {
  name: "",
  cname: "",
  description: "",
  status: "ACTIVE",
  priority: 0,
  triggerType: "HOOK",
  triggerConfig: {
    targetEntity: null,
    action: "UPDATE",
    watchFields: [],
    allowedMakers: [],
    allowedMakerRoles: [],
  },
  steps: [
    {
      order: 1,
      name: "Review",
      approvalType: "ANY",
      approvers: [],
      approverRoles: [],
      checklist: [],
      setValues: [],
      notifications: [],
      stepFields: [],
    },
  ],
  finalAction: { setStatus: "", setFields: {} },
  _id: null,
  mcaPending: false,
  mcaRequestId: null,
};

const McaProcessEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const tabNav = useTabNavigate();
  const [api] = useServerApi();
  const tabActions = useTabActionsOptional();
  const [activeTab, setActiveTab] = useState("basic");
  const [watchRows, setWatchRows] = useState(watchFieldsToRows([]));
  const [saving, setSaving] = useState(false);
  const [processRecord, setProcessRecord] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const form = useForm({ initialValues: defaultValues });
  const processLocked = isEntityMcaLocked(processRecord);

  const mcaSubmit = useMcaSubmitAction({
    api,
    apiEntity,
    mcaEntity: "approval",
    formMode: isNew ? "add" : "edit",
    entity: processRecord,
    isDirty: form.isDirty(),
    onSubmitted: async () => setReloadKey((value) => value + 1),
  });

  const syncTabLabel = (name) => {
    if (!tabActions?.enabled || !name?.trim()) return;
    tabActions.updateTabLabelByKey(
      buildTabKey(location.pathname, location.search),
      `MCA - ${name.trim()}`
    );
  };

  useEffect(() => {
    if (isNew) return;
    const load = async () => {
      try {
        const doc = await api.getById({ apiEntity, id });
        if (!doc) {
          showNotification({
            title: "Process not found",
            message:
              "This approval process no longer exists. Close this tab and open an existing process from the list, or create a new one.",
            color: "red",
          });
          return;
        }
      const steps = migrateFinalActionToSteps(
        doc.steps?.length ? doc.steps : defaultValues.steps,
        doc.finalAction
      );
      const nextValues = {
        name: doc.name ?? "",
        cname: doc.cname ?? "",
        description: doc.description ?? "",
        status: doc.status ?? "ACTIVE",
        priority: doc.priority ?? 0,
        triggerType: doc.triggerType ?? "HOOK",
        triggerConfig: {
          targetEntity: doc.triggerConfig?.targetEntity ?? null,
          action: doc.triggerConfig?.action ?? "UPDATE",
          watchFields: doc.triggerConfig?.watchFields ?? [],
          allowedMakers: doc.triggerConfig?.allowedMakers ?? [],
          allowedMakerRoles: doc.triggerConfig?.allowedMakerRoles ?? [],
        },
        steps: steps.map((s) => ({
          ...s,
          notifications: migrateStepNotifications(s),
          stepFields: migrateStepFields(s),
        })),
        finalAction: defaultValues.finalAction,
        _id: doc._id,
        mcaPending: !!doc.mcaPending,
        mcaRequestId: doc.mcaRequestId ?? null,
      };
      form.setValues(nextValues);
      form.resetDirty(nextValues);
      setProcessRecord(doc);
      setWatchRows(watchFieldsToRows(doc.triggerConfig?.watchFields));
      syncTabLabel(doc.name || doc.code);
      } catch (error) {
        showNotification({
          title: "Error",
          message: error?.message ?? "Failed to load approval process",
          color: "red",
        });
      }
    };
    load();
  }, [id, isNew, reloadKey]);

  useEffect(() => {
    if (isNew) return;
    syncTabLabel(form.values.name);
  }, [form.values.name, isNew, location.pathname, location.search]);

  useEffect(() => {
    if (activeTab === "watch" && form.values.triggerConfig.action !== "UPDATE") {
      setActiveTab("trigger");
    }
  }, [form.values.triggerConfig.action, activeTab]);

  const entityMeta = MCA_ENTITIES.find(
    (e) => e.value === form.values.triggerConfig.targetEntity
  );

  const save = async () => {
    if (processLocked) {
      showNotification({
        title: "Changes blocked",
        message: "This approval process is under approval and cannot be edited.",
        color: "orange",
      });
      return;
    }
    if (!form.values.name?.trim()) {
      showNotification({
        title: "Validation",
        message: "Process name is required",
        color: "red",
      });
      setActiveTab("basic");
      return;
    }
    if (!form.values.triggerConfig.targetEntity) {
      showNotification({
        title: "Validation",
        message: "Target entity is required",
        color: "red",
      });
      setActiveTab("trigger");
      return;
    }
    if (form.values.steps.some((s) => !s.name?.trim())) {
      showNotification({
        title: "Validation",
        message: "Each approval step needs a name",
        color: "red",
      });
      setActiveTab("steps");
      return;
    }
    for (const step of form.values.steps) {
      for (const notification of step.notifications ?? []) {
        const error = validateStepNotification(notification);
        if (error) {
          showNotification({
            title: "Validation",
            message: `${step.name?.trim() || "Step"}: ${error}`,
            color: "red",
          });
          setActiveTab("steps");
          return;
        }
      }
    }

    setSaving(true);
    try {
      const values = buildPayload(form.values, watchRows);
      const res = isNew
        ? await api.add({ apiEntity, values })
        : await api.updateById({ apiEntity, id, values });
      if (!res?.success) {
        throw new Error(getSaveErrorMessage(api, res));
      }
      showNotification({ title: "Saved", message: "Process saved", color: "green" });
      notifyListRefresh(MCA_PROCESS_LIST_PATH);
      if (isNew) {
        const newId = res?.data?._id;
        if (newId) {
          navigate(`/management/mcaProcess/${newId}`, { replace: true });
        }
      } else {
        form.resetDirty(form.values);
        setReloadKey((value) => value + 1);
      }
    } catch (e) {
      showNotification({
        title: "Error",
        message: e?.message ?? "Save failed",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (tabNav.enabled) {
      tabNav.closeActive();
      return;
    }
    navigate("/management/mcaProcess");
  };

  return (
    <Page
      title={isNew ? "New Approval Process" : "Edit Approval Process"}
      actions={
        <Group>
          <Button variant="default" onClick={handleClose}>
            Close
          </Button>
          {mcaSubmit.showMcaSubmit &&
            (mcaSubmit.mcaSubmitDisabledReason ? (
              <Tooltip label={mcaSubmit.mcaSubmitDisabledReason} withArrow>
                <span>
                  <Button
                    color="green"
                    onClick={mcaSubmit.submitForApproval}
                    loading={mcaSubmit.submitting}
                    disabled={!mcaSubmit.canSubmitMcaCreate}
                  >
                    Submit for Approval
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                color="green"
                onClick={mcaSubmit.submitForApproval}
                loading={mcaSubmit.submitting}
                disabled={!mcaSubmit.canSubmitMcaCreate}
              >
                Submit for Approval
              </Button>
            ))}
          <Button onClick={save} loading={saving} disabled={processLocked}>
            Save
          </Button>
        </Group>
      }
    >
      {processRecord && <McaEntityBanner entity={processRecord} />}
      <Box
        sx={
          processLocked
            ? { pointerEvents: "none", opacity: 0.65 }
            : undefined
        }
      >
      <Tabs value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
          <Tabs.Tab value="trigger">Trigger Config</Tabs.Tab>
          <Tabs.Tab value="steps">Approval Steps</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="basic" pt="xl">
          <Paper shadow="md" p="xl" radius={0}>
            <Stack>
              <TextInput
                label="Process name"
                required
                {...form.getInputProps("name")}
              />
              <TextInput label="Chinese name" {...form.getInputProps("cname")} />
              <Textarea label="Description" {...form.getInputProps("description")} />
              <NumberInput
                label="Priority"
                description="Higher priority processes are preferred when multiple match. Conditional processes always beat catch-all processes."
                min={0}
                {...form.getInputProps("priority")}
              />
              <Switch
                label={
                  form.values.status === "DFT"
                    ? "Draft — submit for approval to activate"
                    : "Active"
                }
                checked={form.values.status === "ACTIVE"}
                disabled={form.values.status === "DFT" || processLocked}
                onChange={(e) =>
                  form.setFieldValue(
                    "status",
                    e.currentTarget.checked ? "ACTIVE" : "INACTIVE"
                  )
                }
              />
            </Stack>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="trigger" pt="xl">
          <FormSectionCard title="Trigger Config">
            <Stack>
              <Select
                label="Trigger type"
                data={MCA_TRIGGER_TYPES}
                {...form.getInputProps("triggerType")}
              />
              <Select
                label="Target entity"
                data={MCA_ENTITIES.map((e) => ({ value: e.value, label: e.label }))}
                clearable
                required
                placeholder="Select entity"
                {...form.getInputProps("triggerConfig.targetEntity")}
              />
              <Select
                label="Action"
                data={MCA_ACTIONS}
                {...form.getInputProps("triggerConfig.action")}
              />
            </Stack>
          </FormSectionCard>
          {["CREATE", "UPDATE", "DELETE"].includes(form.values.triggerConfig.action) && (
            <FormSectionCard title="Conditions">
              <Select
                label="Condition logic"
                data={MCA_WATCH_FIELD_LOGIC}
                mb="md"
                {...form.getInputProps("triggerConfig.watchFieldLogic")}
              />
              <McaWatchFieldsEditor
                rows={watchRows}
                onChange={setWatchRows}
                suggestedFields={entityMeta?.watchFields ?? []}
                entityKey={form.values.triggerConfig.targetEntity}
                action={form.values.triggerConfig.action}
              />
            </FormSectionCard>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="steps" pt="xl">
          {activeTab === "steps" && (
            <Paper shadow="md" p="xl" radius={0}>
              <McaStepBuilder
                steps={form.values.steps}
                onChange={(steps) => form.setFieldValue("steps", steps)}
                statusFields={entityMeta?.setValueFields ?? []}
                targetEntity={form.values.triggerConfig?.targetEntity}
              />
            </Paper>
          )}
        </Tabs.Panel>
      </Tabs>
      </Box>
    </Page>
  );
};

export default McaProcessEditor;
